import { Text, Card, Button, Container, Divider, Title, Image, Checkbox, Center, Flex, SimpleGrid, Stack, Grid, Switch, Space } from '@mantine/core';
import classes from './Pricing.module.css';
import { Trans, useTranslation } from "react-i18next";
import { useCart } from "../Cart/CartContext";
import logo from "../../media/iRdrive-logo-nobg.png";
import paypalLogo from "../../media/paypal-logo-white.png"; // Make sure to add this image
import { useState } from 'react';
import styles from './VerticalButton.module.css';

interface VerticalButtonProps {
    header: string;
    subheader: string;
    price: number | null;
    currency: string;
    paycycle: string;
    features: string[];
    button: string;
  }

  interface VdynamicPricingListProps {
    header: string;
    subheader: string;
    priceMonthly: number | null;
    priceYearly: number | null;
    currency: string;
    paycycle: string;
    features: string[];
    button: string;
  }
  
  export function VerticalButton({
    header,
    subheader,
    price,
    currency,
    paycycle,
    features,
    button
  }: VerticalButtonProps) {
    const { openCart } = useCart();
    
    const dynamicFeatures = features.map((feature, index) => (
      <Checkbox
        key={index}
        size="xl"
        variant="outline"
        label={feature}
        checked={true}
        onChange={(event) => event.preventDefault()}
        className={styles.checkbox}
        classNames={{label: styles.featureLabel}}
        styles={{
          input: { border: "0px solid #228be6", backgroundColor: "transparent" }
        }}
      />
    ));
        
    return (
      <Card
        withBorder
        padding="35px"
        radius="md"
        className={styles.pricingCard}
      >
        <Card.Section className={styles.section}>
          <Container className={styles.container}>
            <Container className={`${styles.container} ${styles.headerContainer}`}>
              <Title ta="center" className={styles.header}>
                {header}
              </Title>
              <Text ta="center" c="dimmed" className={styles.featureLabel}>
                {subheader}
              </Text>
              <Container className={`${styles.container} ${styles.priceContainer}`}>
                <Title ta="center" className={styles.header}>
                  {price} {currency}
                </Title>
                <Text ta="center" c="dimmed" className={styles.featureLabel}>
                  {paycycle}
                </Text>
              </Container>
            </Container>
            <Center>
              <Divider my="sm" w="80%" />
            </Center>
            <Container className={styles.container}>
              {dynamicFeatures}
            </Container>
          </Container>
        </Card.Section>
        <Button color="#004aba" fullWidth onClick={openCart}>
          <Trans i18nKey="cta.main.checkout" />
        </Button>
      </Card>
    );
  }

  export function PricingSelection(dynamicPricingList: VdynamicPricingListProps[]) {
    const [checked, setChecked] = useState(false);
    
    const dynamicPricingCards = dynamicPricingList.map((pricing, index) => (
      <VerticalButton 
        key={index}
        button={pricing.button}
        header={pricing.header} 
        subheader={pricing.subheader} 
        price={checked ? pricing.priceYearly : pricing.priceMonthly}
        currency={pricing.currency} 
        paycycle={checked ? "/year" : "/month"}
        features={pricing.features} 
      />
    ));
  
    return (
      <>
        <Center>
          <Flex align="center" gap="md" wrap="nowrap">
            <Text 
              w={100}
              size="sm" 
              fw={checked ? 400 : 700}
              c={checked ? "gray" : "blue"}
              className={`${styles.alignRight} ${styles.switchLabel}`}
            >
              Monthly
            </Text>
          
            <Switch 
              checked={checked} 
              onChange={(event) => setChecked(event.currentTarget.checked)}
              size="md"
            />
            
            <Text 
              w={100}
              size="sm" 
              fw={checked ? 700 : 400}
              c={checked ? "blue" : "gray"}
              className={`${styles.alignLeft} ${styles.switchLabel}`}
            >
              Yearly
            </Text>
          </Flex>
        </Center>
        
        <Space h="md" />
        
        <Center>
          <SimpleGrid 
            cols={{ base: 1, sm: 2, lg: 3 }} 
            spacing={{ base: "md", sm: "lg", lg: "xl" }}
            style={{ width: '100%', maxWidth: '92em' }}
          > 
            {dynamicPricingCards}
          </SimpleGrid>
        </Center>
      </>
    );
  }

  export function BigButton({
    header,
    subheader,
    price,
    currency,
    paycycle,
    features,
    button
  }: VerticalButtonProps) {
    const { t } = useTranslation("translation");
    const { openCart } = useCart();
    
    const dynamicFeatures = features.map((feature, index) => (
      <div key={index} className={styles.featureItem}>
        <div className={styles.checkmark}>
          <svg width="24" height="18" viewBox="0 0 26 19" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9.20535 18.7678L0.417847 9.98029L2.61472 7.78342L9.20535 14.374L23.3501 0.229248L25.547 2.42612L9.20535 18.7678Z" fill="white"/>
          </svg>
        </div>
        <Text className={styles.featureText}>{t(feature)}</Text>
      </div>
    ));
        
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <Text className={styles.headerText}>
            <Trans i18nKey="pricing.founderEdition" />
          </Text>
        </div>
        
        <div className={styles.content} onClick={openCart}>
          {/* Logo background that fills entire area */}
          <div className={styles.logoBackground}>
            <Image src={logo} alt="Logo background" className={styles.logoImage} />
          </div>
          
          {/* Left section with title and features */}
          <div className={styles.leftSection}>
            <div className={styles.mainTitle}>
              <Text className={styles.mainTitleIrdrive}>
                iRDRIVE
              </Text>
              <Text className={styles.mainTitleAction}>
                <Trans i18nKey="pricing.action" />
              </Text>
            </div>
            
            <div className={styles.goldLine}></div>
            
            <div className={styles.featuresContainer}>
              {dynamicFeatures}
            </div>
          </div>
          
          {/* Right section with price and button */}
          <div className={styles.rightSection}>
            <div className={styles.priceBox}>
              <Text className={styles.priceText}>
                {price}{currency}
              </Text>
              <Text className={styles.vatText}>
                <Trans i18nKey="pricing.vatIncluded" />
              </Text>
            </div>
            
            <Button className={styles.checkoutButton}>
              <div className={styles.paypalButtonContent}>
                <span className={styles.payButtonText}>
                  <Trans i18nKey="cta.main.checkout2" />
                </span>
                <Image src={paypalLogo} alt="PayPal" className={styles.paypalLogo} />
              </div>
            </Button>
          </div>
        </div>
      </div>
    );
  }